import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiCheckCircleBold, PiClockBold, PiEyeBold, PiGraduationCapBold, PiLinkBold } from "react-icons/pi";
import client from "../../api/client";
import { downloadSubmissionFile, submissionViewerPath } from "../../utils/submissionFile";
import SubmissionFilePreview from "./SubmissionFilePreview";

const GRADE_COLOR = {
  A: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  B: "bg-blue-50 text-blue-700 border border-blue-100",
  C: "bg-amber-50 text-amber-700 border border-amber-100",
  D: "bg-orange-50 text-orange-700 border border-orange-100",
  F: "bg-rose-50 text-rose-700 border border-rose-100",
};

function SubmissionCard({ sub, itemType, onGrade }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 hover:border-[#F05A19]/30 transition-all p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 duration-200">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2.5 py-1 rounded-md">
            {sub.label || (itemType === "assignment" ? "Assignment" : "Project")}
          </span>
          {sub.grade && (
            <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${GRADE_COLOR[sub.grade]}`}>
              Grade: {sub.grade}
            </span>
          )}
        </div>
        <h3 className="text-base font-bold text-gray-800">{sub.title || sub.label}</h3>
        {sub.description && (
          <p className="text-sm text-gray-500 line-clamp-2">{sub.description}</p>
        )}
        {!sub.description && (sub.file_link || sub.file_download_url) && (
          <p className="text-sm text-gray-400 italic">File submission — no typed answer</p>
        )}

        {/* Student Info & Metadata */}
        <div className="flex items-center gap-4 text-xs text-gray-400 pt-2 flex-wrap">
          <span className="font-medium text-gray-600">
            Student: {sub.user?.firstname} {sub.user?.lastname} ({sub.user?.email})
          </span>
          <span>•</span>
          <span>Submitted on: {new Date(sub.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-row md:flex-col gap-3 flex-shrink-0 items-stretch md:items-end justify-start md:justify-center">
        {sub.file_download_url && (
          <a
            href={submissionViewerPath(sub)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-xs font-semibold text-white bg-[#F05A19] hover:bg-[#d14d14] rounded-xl px-4 py-2.5 transition-colors text-center"
          >
            <PiEyeBold size={16} />
            View File
          </a>
        )}
        {sub.file_download_url && (
          <button
            type="button"
            onClick={() =>
              downloadSubmissionFile(sub).catch(() => toast.error("Could not download this file."))
            }
            className="flex items-center justify-center gap-2 text-xs font-semibold text-[#F05A19] bg-[#FEF0EB] hover:bg-[#FDD8CC] rounded-xl px-4 py-2.5 transition-colors text-center"
          >
            <PiLinkBold size={16} />
            Download File
          </button>
        )}
        {sub.file_link && (
          <a
            href={sub.file_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-xs font-semibold text-[#F05A19] bg-[#FEF0EB] hover:bg-[#FDD8CC] rounded-xl px-4 py-2.5 transition-colors text-center"
          >
            <PiLinkBold size={16} />
            View Student Work
          </a>
        )}
        <button
          onClick={() => onGrade(sub)}
          className="flex items-center justify-center gap-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-xl px-4 py-2.5 transition-colors"
        >
          <PiGraduationCapBold size={16} />
          {sub.grade ? "Edit Grade & Feedback" : "Evaluate & Grade"}
        </button>
      </div>
    </div>
  );
}

export default function InstructorSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemType, setItemType] = useState("project"); // project, assignment
  const [activeTab, setActiveTab] = useState("pending"); // pending, graded, all
  const [search, setSearch] = useState("");
  const [selectedSub, setSelectedSub] = useState(null);

  // Grading form state
  const [grade, setGrade] = useState("A");
  const [feedback, setFeedback] = useState("");
  const [submittingGrade, setSubmittingGrade] = useState(false);

  const fetchSubmissions = async (type) => {
    setLoading(true);
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    try {
      const url = type === "assignment" ? "/instructor/exercise-submissions" : "/instructor/submissions";
      const res = await client.get(url);
      setSubmissions(res.data || []);
    } catch (err) {
      toast.error("Failed to load student submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions(itemType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType]);

  const handleOpenGradeModal = (sub) => {
    setSelectedSub(sub);
    setGrade(sub.grade || "A");
    setFeedback(sub.instructor_feedback || "");
  };

  const handleCloseGradeModal = () => {
    setSelectedSub(null);
    setGrade("A");
    setFeedback("");
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) {
      toast.error("Please enter instructor feedback.");
      return;
    }
    setSubmittingGrade(true);
    try {
      await client.post(`/submissions/${selectedSub.id}/feedback`, {
        grade,
        instructor_feedback: feedback,
      });
      toast.success("Feedback & Grade submitted successfully!");
      fetchSubmissions(itemType);
      handleCloseGradeModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit feedback.");
    } finally {
      setSubmittingGrade(false);
    }
  };

  // Filter submissions
  const filtered = submissions.filter((sub) => {
    const isGraded = !!sub.grade;
    if (activeTab === "pending" && isGraded) return false;
    if (activeTab === "graded" && !isGraded) return false;

    if (search.trim()) {
      const query = search.toLowerCase();
      const studentName = `${sub.user?.firstname} ${sub.user?.lastname}`.toLowerCase();
      const itemLabel = sub.label?.toLowerCase() || "";
      const subTitle = sub.title?.toLowerCase() || "";
      return studentName.includes(query) || itemLabel.includes(query) || subTitle.includes(query);
    }
    return true;
  });

  // When viewing assignments, group by class (schedule) for the "class by class" view
  const groupedByClass = itemType === "assignment"
    ? filtered.reduce((acc, sub) => {
        const schedule = sub.submittable?.schedule;
        const key = schedule?.id ?? "unknown";
        if (!acc[key]) {
          acc[key] = { schedule, items: [] };
        }
        acc[key].items.push(sub);
        return acc;
      }, {})
    : null;

  return (
    <div className="py-8 px-4 md:px-8 max-w-6xl mx-auto">
      {/* Title */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Student Submissions</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review, evaluate, and grade {itemType === "assignment" ? "assignment" : "project"} submissions from your assigned cohorts.
          </p>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search students or projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-80 px-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 placeholder-gray-400 bg-white"
          />
        </div>
      </div>

      {/* Type toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => { setItemType("project"); setActiveTab("pending"); }}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            itemType === "project"
              ? "bg-slate-900 text-white"
              : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          Projects
        </button>
        <button
          onClick={() => { setItemType("assignment"); setActiveTab("pending"); }}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            itemType === "assignment"
              ? "bg-slate-900 text-white"
              : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          Assignments
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "pending"
              ? "border-[#F05A19] text-[#F05A19]"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <PiClockBold size={16} />
          Pending Review
          <span className="ml-1 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
            {submissions.filter(s => !s.grade).length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("graded")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "graded"
              ? "border-[#F05A19] text-[#F05A19]"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <PiCheckCircleBold size={16} />
          Graded
          <span className="ml-1 bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-0.5 rounded-full">
            {submissions.filter(s => s.grade).length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "all"
              ? "border-[#F05A19] text-[#F05A19]"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <PiGraduationCapBold size={16} />
          All Submissions
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse flex justify-between">
              <div className="space-y-2 w-1/3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
              <div className="h-10 bg-gray-100 rounded w-24" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center shadow-sm">
          <p className="text-4xl mb-3">📁</p>
          <h3 className="font-semibold text-gray-700 text-base">No submissions found</h3>
          <p className="text-sm text-gray-400 mt-1">There are no submissions matching your filters.</p>
        </div>
      ) : itemType === "assignment" ? (
        <div className="space-y-8">
          {Object.values(groupedByClass).map(({ schedule, items }) => (
            <div key={schedule?.id ?? "unknown"}>
              <div className="flex items-baseline gap-2 mb-3 px-1">
                <h2 className="text-sm font-bold text-gray-800">
                  {schedule?.topic || "Unassigned Class"}
                </h2>
                {schedule?.start_time && (
                  <span className="text-xs text-gray-400">
                    {new Date(schedule.start_time).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="grid gap-4">
                {items.map((sub) => (
                  <SubmissionCard key={sub.id} sub={sub} itemType={itemType} onGrade={handleOpenGradeModal} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((sub) => (
            <SubmissionCard key={sub.id} sub={sub} itemType={itemType} onGrade={handleOpenGradeModal} />
          ))}
        </div>
      )}

      {/* Grading Modal */}
      {selectedSub && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden border border-gray-100 flex flex-col">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Evaluate Student Submission</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Student: {selectedSub.user?.firstname} {selectedSub.user?.lastname}
                </p>
              </div>
              <button
                onClick={handleCloseGradeModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleGradeSubmit} className="p-6 space-y-5 overflow-y-auto">
              {/* Submission view */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100 max-h-[50vh] overflow-y-auto">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {selectedSub.label || (itemType === "assignment" ? "Assignment" : "Project")} submission
                  </span>
                  <h4 className="text-sm font-bold text-gray-800">{selectedSub.title || selectedSub.label}</h4>
                </div>

                {selectedSub.description && (
                  <div>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Typed answer
                    </span>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap mt-1 bg-white border border-gray-100 rounded-lg p-3">
                      {selectedSub.description}
                    </p>
                  </div>
                )}

                {selectedSub.file_download_url && (
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                        Uploaded file
                      </span>
                      <a
                        href={submissionViewerPath(selectedSub)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[11px] font-semibold text-[#F05A19] hover:underline"
                      >
                        <PiEyeBold size={12} /> Open in new page
                      </a>
                    </div>
                    <div className="mt-1">
                      <SubmissionFilePreview submission={selectedSub} />
                    </div>
                  </div>
                )}

                {selectedSub.file_link && (
                  <div>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Submitted link
                    </span>
                    <div className="mt-1">
                      <SubmissionFilePreview url={selectedSub.file_link} />
                    </div>
                  </div>
                )}
              </div>

              {/* Grade */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Grade</label>
                <p className="text-xs text-gray-500 mb-2">A 90–100 · B 80–89 · C 70–79 · D 60–69 · F below 60. A capstone needs A, B or C to pass.</p>
                <div className="grid grid-cols-5 gap-2">
                  {["A", "B", "C", "D", "F"].map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGrade(g)}
                      className={`py-3 rounded-xl text-sm font-bold border transition-all ${
                        grade === g
                          ? "bg-[#FEF0EB] text-[#F05A19] border-[#F05A19]"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Feedback */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Feedback</label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Provide structured constructive feedback for the student..."
                  required
                  rows={5}
                  className="w-full border border-gray-200 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 resize-none bg-white"
                />
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={handleCloseGradeModal}
                  className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingGrade}
                  className="flex-1 py-3 px-4 bg-[#F05A19] hover:bg-[#d14d14] text-white rounded-xl text-sm font-semibold shadow-md shadow-orange-100 transition-colors disabled:opacity-60"
                >
                  {submittingGrade ? "Submitting..." : "Submit Review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
